import type { Shift, Model } from '../schema';
import { getMonthDateBoundaries } from './common';
import { DateTime } from 'luxon';

export interface ModelEarningsData {
  modelName: string;
  total2MonthsAgo: number;
  total1MonthAgo: number;
  totalThisMonth: number;
  dailyAvg1MonthAgo: number;
  dailyAvgThisMonth: number;
  avg6to2: number;
  avg2to10: number;
  avg10to6: number;
  avg6amTo6pm: number;
  avg6pmTo6am: number;
  prev6to2: number;
  prev2to10: number;
  prev10to6: number;
  prev6amTo6pm: number;
  prev6pmTo6am: number;
  // PPV averages for current month
  ppvAvg6to2: number;
  ppvAvg2to10: number;
  ppvAvg10to6: number;
  ppvAvg6amTo6pm: number;
  ppvAvg6pmTo6am: number;
  // NOPPV averages for current month
  noppvAvg6to2: number;
  noppvAvg2to10: number;
  noppvAvg10to6: number;
  noppvAvg6amTo6pm: number;
  noppvAvg6pmTo6am: number;
  // PPV averages for previous month
  prevPpvAvg6to2: number;
  prevPpvAvg2to10: number;
  prevPpvAvg10to6: number;
  prevPpvAvg6amTo6pm: number;
  prevPpvAvg6pmTo6am: number;
  // NOPPV averages for previous month
  prevNoppvAvg6to2: number;
  prevNoppvAvg2to10: number;
  prevNoppvAvg10to6: number;
  prevNoppvAvg6amTo6pm: number;
  prevNoppvAvg6pmTo6am: number;
}

export interface DateRanges {
  startDate: DateTime;
  month2Ago: string;
  month1Ago: string;
  currentMonth: string;
}

const getShiftTimeSlot = (start_date_time: DateTime, end_date_time: DateTime): string | null => {
  const startHour = start_date_time.hour;
  const endHour = end_date_time.hour;

  if (startHour === 6 && endHour === 14) return '6-2';
  if (startHour === 14 && endHour === 22) return '2-10';
  if (startHour === 22 && (endHour === 6 || endHour === 30)) return '10-6';
  if (startHour === 6 && endHour === 18) return '6am-6pm';
  if (startHour === 18 && (endHour === 6 || endHour === 30)) return '6pm-6am';

  return null;
};

export const calculateDateRanges = (currentDate?: DateTime): DateRanges => {
  const now = currentDate || DateTime.utc();

  const twoMonthsAgo = now.minus({ month: 2 }).set({ day: 1, hour: 6, minute: 0, second: 0, millisecond: 0 });
  const oneMonthAgo = now.minus({ month: 1 }).set({ day: 1, hour: 6, minute: 0, second: 0, millisecond: 0 });

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return {
    startDate: twoMonthsAgo,
    month2Ago: monthNames[twoMonthsAgo.month - 1],
    month1Ago: monthNames[oneMonthAgo.month - 1],
    currentMonth: monthNames[now.month - 1]
  };
};

export const calculateModelEarnings = (shifts: Shift[], models: Model[], currentDate?: DateTime): ModelEarningsData[] => {
  if (shifts.length === 0 || models.length === 0) {
    return [];
  }

  const now = currentDate || DateTime.utc();

  const { start: currentMonthStart, end: nextMonthStart } = getMonthDateBoundaries(now);

  const current2MonthsAgoStart = currentMonthStart.minus({ month: 2 });
  const current1MonthAgoStart = currentMonthStart.minus({ month: 1 });

  const modelData: ModelEarningsData[] = [];

  const shiftsModels = models.filter(model =>
    model.groups && model.groups.includes('Shifts') && model.account_type != "Test"
  );

  shiftsModels.forEach(model => {
    const modelShifts = shifts.filter(shift => shift.model_id === model.id);

    const shifts2MonthsAgo = modelShifts.filter(shift =>
      shift.start_date_time >= current2MonthsAgoStart && shift.start_date_time < current1MonthAgoStart
    );
    const shifts1MonthAgo = modelShifts.filter(shift =>
      shift.start_date_time >= current1MonthAgoStart && shift.start_date_time < currentMonthStart
    );
    const shiftsThisMonth = modelShifts.filter(shift =>
      shift.start_date_time >= currentMonthStart && shift.start_date_time < nextMonthStart
    );

    const total2MonthsAgo = shifts2MonthsAgo.reduce((sum, shift) => sum + (shift.ppv_earned || 0), 0);
    const total1MonthAgo = shifts1MonthAgo.reduce((sum, shift) => sum + (shift.ppv_earned || 0), 0);
    const totalThisMonth = shiftsThisMonth.reduce((sum, shift) => sum + (shift.ppv_earned || 0), 0);

    const daysIn1MonthAgo = current1MonthAgoStart.daysInMonth || 1;
    const daysInThisMonth = currentMonthStart.daysInMonth || 1;

    const dailyAvg1MonthAgo = shifts1MonthAgo.length > 0 ? total1MonthAgo / daysIn1MonthAgo : 0;
    const dailyAvgThisMonth = shiftsThisMonth.length > 0 ? totalThisMonth / daysInThisMonth : 0;

    const timeSlotTotalsThisMonth = {
      '6-2': { ppvTotal: 0, noppvTotal: 0, count: 0 },
      '2-10': { ppvTotal: 0, noppvTotal: 0, count: 0 },
      '10-6': { ppvTotal: 0, noppvTotal: 0, count: 0 },
      '6am-6pm': { ppvTotal: 0, noppvTotal: 0, count: 0 },
      '6pm-6am': { ppvTotal: 0, noppvTotal: 0, count: 0 }
    };

    const timeSlotTotalsLastMonth = {
      '6-2': { ppvTotal: 0, noppvTotal: 0, count: 0 },
      '2-10': { ppvTotal: 0, noppvTotal: 0, count: 0 },
      '10-6': { ppvTotal: 0, noppvTotal: 0, count: 0 },
      '6am-6pm': { ppvTotal: 0, noppvTotal: 0, count: 0 },
      '6pm-6am': { ppvTotal: 0, noppvTotal: 0, count: 0 }
    };

    shiftsThisMonth.forEach(shift => {
      const timeSlot = getShiftTimeSlot(shift.start_date_time, shift.end_date_time);
      if (timeSlot && timeSlotTotalsThisMonth[timeSlot as keyof typeof timeSlotTotalsThisMonth]) {
        timeSlotTotalsThisMonth[timeSlot as keyof typeof timeSlotTotalsThisMonth].ppvTotal += (shift.ppv_earned || 0);
        timeSlotTotalsThisMonth[timeSlot as keyof typeof timeSlotTotalsThisMonth].noppvTotal += (shift.noppv_earned || 0);
        timeSlotTotalsThisMonth[timeSlot as keyof typeof timeSlotTotalsThisMonth].count += 1;
      }
    });

    shifts1MonthAgo.forEach(shift => {
      const timeSlot = getShiftTimeSlot(shift.start_date_time, shift.end_date_time);
      if (timeSlot && timeSlotTotalsLastMonth[timeSlot as keyof typeof timeSlotTotalsLastMonth]) {
        timeSlotTotalsLastMonth[timeSlot as keyof typeof timeSlotTotalsLastMonth].ppvTotal += (shift.ppv_earned || 0);
        timeSlotTotalsLastMonth[timeSlot as keyof typeof timeSlotTotalsLastMonth].noppvTotal += (shift.noppv_earned || 0);
        timeSlotTotalsLastMonth[timeSlot as keyof typeof timeSlotTotalsLastMonth].count += 1;
      }
    });

    // Current month total averages (for backward compatibility)
    const avg6to2 = timeSlotTotalsThisMonth['6-2'].count > 0 ? (timeSlotTotalsThisMonth['6-2'].ppvTotal + timeSlotTotalsThisMonth['6-2'].noppvTotal) / timeSlotTotalsThisMonth['6-2'].count : 0;
    const avg2to10 = timeSlotTotalsThisMonth['2-10'].count > 0 ? (timeSlotTotalsThisMonth['2-10'].ppvTotal + timeSlotTotalsThisMonth['2-10'].noppvTotal) / timeSlotTotalsThisMonth['2-10'].count : 0;
    const avg10to6 = timeSlotTotalsThisMonth['10-6'].count > 0 ? (timeSlotTotalsThisMonth['10-6'].ppvTotal + timeSlotTotalsThisMonth['10-6'].noppvTotal) / timeSlotTotalsThisMonth['10-6'].count : 0;
    const avg6amTo6pm = timeSlotTotalsThisMonth['6am-6pm'].count > 0 ? (timeSlotTotalsThisMonth['6am-6pm'].ppvTotal + timeSlotTotalsThisMonth['6am-6pm'].noppvTotal) / timeSlotTotalsThisMonth['6am-6pm'].count : 0;
    const avg6pmTo6am = timeSlotTotalsThisMonth['6pm-6am'].count > 0 ? (timeSlotTotalsThisMonth['6pm-6am'].ppvTotal + timeSlotTotalsThisMonth['6pm-6am'].noppvTotal) / timeSlotTotalsThisMonth['6pm-6am'].count : 0;

    // Previous month total averages (for backward compatibility)
    const prev6to2 = timeSlotTotalsLastMonth['6-2'].count > 0 ? (timeSlotTotalsLastMonth['6-2'].ppvTotal + timeSlotTotalsLastMonth['6-2'].noppvTotal) / timeSlotTotalsLastMonth['6-2'].count : 0;
    const prev2to10 = timeSlotTotalsLastMonth['2-10'].count > 0 ? (timeSlotTotalsLastMonth['2-10'].ppvTotal + timeSlotTotalsLastMonth['2-10'].noppvTotal) / timeSlotTotalsLastMonth['2-10'].count : 0;
    const prev10to6 = timeSlotTotalsLastMonth['10-6'].count > 0 ? (timeSlotTotalsLastMonth['10-6'].ppvTotal + timeSlotTotalsLastMonth['10-6'].noppvTotal) / timeSlotTotalsLastMonth['10-6'].count : 0;
    const prev6amTo6pm = timeSlotTotalsLastMonth['6am-6pm'].count > 0 ? (timeSlotTotalsLastMonth['6am-6pm'].ppvTotal + timeSlotTotalsLastMonth['6am-6pm'].noppvTotal) / timeSlotTotalsLastMonth['6am-6pm'].count : 0;
    const prev6pmTo6am = timeSlotTotalsLastMonth['6pm-6am'].count > 0 ? (timeSlotTotalsLastMonth['6pm-6am'].ppvTotal + timeSlotTotalsLastMonth['6pm-6am'].noppvTotal) / timeSlotTotalsLastMonth['6pm-6am'].count : 0;

    // Current month PPV averages
    const ppvAvg6to2 = timeSlotTotalsThisMonth['6-2'].count > 0 ? timeSlotTotalsThisMonth['6-2'].ppvTotal / timeSlotTotalsThisMonth['6-2'].count : 0;
    const ppvAvg2to10 = timeSlotTotalsThisMonth['2-10'].count > 0 ? timeSlotTotalsThisMonth['2-10'].ppvTotal / timeSlotTotalsThisMonth['2-10'].count : 0;
    const ppvAvg10to6 = timeSlotTotalsThisMonth['10-6'].count > 0 ? timeSlotTotalsThisMonth['10-6'].ppvTotal / timeSlotTotalsThisMonth['10-6'].count : 0;
    const ppvAvg6amTo6pm = timeSlotTotalsThisMonth['6am-6pm'].count > 0 ? timeSlotTotalsThisMonth['6am-6pm'].ppvTotal / timeSlotTotalsThisMonth['6am-6pm'].count : 0;
    const ppvAvg6pmTo6am = timeSlotTotalsThisMonth['6pm-6am'].count > 0 ? timeSlotTotalsThisMonth['6pm-6am'].ppvTotal / timeSlotTotalsThisMonth['6pm-6am'].count : 0;

    // Current month NOPPV averages
    const noppvAvg6to2 = timeSlotTotalsThisMonth['6-2'].count > 0 ? timeSlotTotalsThisMonth['6-2'].noppvTotal / timeSlotTotalsThisMonth['6-2'].count : 0;
    const noppvAvg2to10 = timeSlotTotalsThisMonth['2-10'].count > 0 ? timeSlotTotalsThisMonth['2-10'].noppvTotal / timeSlotTotalsThisMonth['2-10'].count : 0;
    const noppvAvg10to6 = timeSlotTotalsThisMonth['10-6'].count > 0 ? timeSlotTotalsThisMonth['10-6'].noppvTotal / timeSlotTotalsThisMonth['10-6'].count : 0;
    const noppvAvg6amTo6pm = timeSlotTotalsThisMonth['6am-6pm'].count > 0 ? timeSlotTotalsThisMonth['6am-6pm'].noppvTotal / timeSlotTotalsThisMonth['6am-6pm'].count : 0;
    const noppvAvg6pmTo6am = timeSlotTotalsThisMonth['6pm-6am'].count > 0 ? timeSlotTotalsThisMonth['6pm-6am'].noppvTotal / timeSlotTotalsThisMonth['6pm-6am'].count : 0;

    // Previous month PPV averages
    const prevPpvAvg6to2 = timeSlotTotalsLastMonth['6-2'].count > 0 ? timeSlotTotalsLastMonth['6-2'].ppvTotal / timeSlotTotalsLastMonth['6-2'].count : 0;
    const prevPpvAvg2to10 = timeSlotTotalsLastMonth['2-10'].count > 0 ? timeSlotTotalsLastMonth['2-10'].ppvTotal / timeSlotTotalsLastMonth['2-10'].count : 0;
    const prevPpvAvg10to6 = timeSlotTotalsLastMonth['10-6'].count > 0 ? timeSlotTotalsLastMonth['10-6'].ppvTotal / timeSlotTotalsLastMonth['10-6'].count : 0;
    const prevPpvAvg6amTo6pm = timeSlotTotalsLastMonth['6am-6pm'].count > 0 ? timeSlotTotalsLastMonth['6am-6pm'].ppvTotal / timeSlotTotalsLastMonth['6am-6pm'].count : 0;
    const prevPpvAvg6pmTo6am = timeSlotTotalsLastMonth['6pm-6am'].count > 0 ? timeSlotTotalsLastMonth['6pm-6am'].ppvTotal / timeSlotTotalsLastMonth['6pm-6am'].count : 0;

    // Previous month NOPPV averages
    const prevNoppvAvg6to2 = timeSlotTotalsLastMonth['6-2'].count > 0 ? timeSlotTotalsLastMonth['6-2'].noppvTotal / timeSlotTotalsLastMonth['6-2'].count : 0;
    const prevNoppvAvg2to10 = timeSlotTotalsLastMonth['2-10'].count > 0 ? timeSlotTotalsLastMonth['2-10'].noppvTotal / timeSlotTotalsLastMonth['2-10'].count : 0;
    const prevNoppvAvg10to6 = timeSlotTotalsLastMonth['10-6'].count > 0 ? timeSlotTotalsLastMonth['10-6'].noppvTotal / timeSlotTotalsLastMonth['10-6'].count : 0;
    const prevNoppvAvg6amTo6pm = timeSlotTotalsLastMonth['6am-6pm'].count > 0 ? timeSlotTotalsLastMonth['6am-6pm'].noppvTotal / timeSlotTotalsLastMonth['6am-6pm'].count : 0;
    const prevNoppvAvg6pmTo6am = timeSlotTotalsLastMonth['6pm-6am'].count > 0 ? timeSlotTotalsLastMonth['6pm-6am'].noppvTotal / timeSlotTotalsLastMonth['6pm-6am'].count : 0;

    modelData.push({
      modelName: model.name,
      total2MonthsAgo,
      total1MonthAgo,
      totalThisMonth,
      dailyAvg1MonthAgo,
      dailyAvgThisMonth,
      avg6to2,
      avg2to10,
      avg10to6,
      avg6amTo6pm,
      avg6pmTo6am,
      prev6to2,
      prev2to10,
      prev10to6,
      prev6amTo6pm,
      prev6pmTo6am,
      // PPV averages for current month
      ppvAvg6to2,
      ppvAvg2to10,
      ppvAvg10to6,
      ppvAvg6amTo6pm,
      ppvAvg6pmTo6am,
      // NOPPV averages for current month
      noppvAvg6to2,
      noppvAvg2to10,
      noppvAvg10to6,
      noppvAvg6amTo6pm,
      noppvAvg6pmTo6am,
      // PPV averages for previous month
      prevPpvAvg6to2,
      prevPpvAvg2to10,
      prevPpvAvg10to6,
      prevPpvAvg6amTo6pm,
      prevPpvAvg6pmTo6am,
      // NOPPV averages for previous month
      prevNoppvAvg6to2,
      prevNoppvAvg2to10,
      prevNoppvAvg10to6,
      prevNoppvAvg6amTo6pm,
      prevNoppvAvg6pmTo6am
    });
  });

  return modelData.sort((a, b) => a.modelName.localeCompare(b.modelName));
};